import { FocusEvent, useMemo } from "react";
import { useSelector } from "react-redux";
import { FormikErrors, FormikTouched, getIn } from "formik";
import get from "lodash/get";
import Alert from "react-bootstrap/Alert";
import { AXNUIContentCustomAttributeField, SelectOption } from "@iddataweb/idw-common-ui/types";
import { AttributeTypeField } from "@idw-types/data";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { useParamSelector } from "@hooks/use-param-selector";
import { CustomDropdownField } from "@containers/inputs/custom-dropdown-field/custom-dropdown-field";
import { CustomTextField } from "@containers/inputs/custom-text-field/custom-text-field";
import { CustomRadioButtonField } from "@containers/inputs/custom-radio-button-field/custom-radio-button-field";
import { CustomAttributeTypeFieldInputLabel } from "../custom-attribute-field-input-label/custom-attribute-field-input-label";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { shouldHighlightInput } from "@redux/selectors/verification-form-selectors";
import { ReadOnlyAttributeInput } from "@containers/inputs/read-only-attribute-input/read-only-attribute-input";
import { PickerAttributeInput } from "@containers/inputs/picker-attribute-input/picker-attribute-input";
import { CustomAttributeTypeFieldTemplateTypes } from "@iddataweb/idw-common-ui/enums";

type CustomAttributeFieldContainerProps = {
    attribute: AttributeFormState;
    attributeTypeField: AttributeTypeField;
    errors: FormikErrors<any>,
    touched: FormikTouched<any>,
    isSubmitting: boolean;
    customAttributeFieldData: {
        options: Array<SelectOption>;
        content: AXNUIContentCustomAttributeField;
    };
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;

    onSetSubmitValue: (data: { inputData: { [key: string]: string } }) => void;
    onAddNewSubmitValue: (data: { inputData: { [key: string]: string } }) => void;
    onSetPickerValue: (payload: any) => void;
};

export const CustomAttributeFieldContainer = (props: CustomAttributeFieldContainerProps) => {
    const {
        attribute,
        attributeTypeField,
        touched,
        errors,
        isSubmitting,
        customAttributeFieldData,
        onBlur,
        setFieldTouched,
        setFieldValue,
        onSetSubmitValue,
        onSetPickerValue,
        onAddNewSubmitValue
    } = props;

    const content = useSelector(getLanguageContent);
    const fieldTouched = getIn(touched, attributeTypeField.name);
    const fieldError = getIn(errors, attributeTypeField.name);
    const fieldIsInvalid = !!fieldError && !!fieldTouched && !attribute.inBandPinMode;
    const fieldErrorMessage = fieldError ? (get(content, fieldError) || fieldError): null;
    const fieldShouldBeHighlighted = useParamSelector(shouldHighlightInput, attribute);

    const InputComponent = useMemo(() => {
        switch (attributeTypeField.templateName) {
            case CustomAttributeTypeFieldTemplateTypes.CUSTOM_NUMERIC:
                return (
                    <CustomTextField
                        attributeFieldContent={customAttributeFieldData.content}
                        attribute={attribute}
                        attributeTypeField={attributeTypeField}
                        fieldTouched={fieldTouched}
                        fieldError={fieldErrorMessage}
                        fieldIsInvalid={fieldIsInvalid}
                        fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                        isSubmitting={isSubmitting}
                        type={"number"}
                        onBlur={onBlur}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        onSetSubmitValue={onSetSubmitValue}
                    />
                );
            case CustomAttributeTypeFieldTemplateTypes.CUSTOM_ALPHA_NUMERIC:
                return (
                    <CustomTextField
                        attributeFieldContent={customAttributeFieldData.content}
                        attribute={attribute}
                        attributeTypeField={attributeTypeField}
                        fieldTouched={fieldTouched}
                        fieldError={fieldErrorMessage}
                        fieldIsInvalid={fieldIsInvalid}
                        fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                        isSubmitting={isSubmitting}
                        type={"text"}
                        onBlur={onBlur}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        onSetSubmitValue={onSetSubmitValue}
                    />
                );
            case CustomAttributeTypeFieldTemplateTypes.CUSTOM_DROPDOWN:
                return (
                    <CustomDropdownField
                        attributeFieldContent={customAttributeFieldData.content}
                        attributeFieldOptions={customAttributeFieldData.options}
                        attribute={attribute}
                        attributeTypeField={attributeTypeField}
                        fieldTouched={fieldTouched}
                        fieldError={fieldErrorMessage}
                        fieldIsInvalid={fieldIsInvalid}
                        fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                        isSubmitting={isSubmitting}
                        onBlur={onBlur}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        onSetSubmitValue={onSetSubmitValue}
                    />
                );
            case CustomAttributeTypeFieldTemplateTypes.CUSTOM_RADIO_BUTTON:
                return (
                    <CustomRadioButtonField
                        attributeFieldOptions={customAttributeFieldData.options}
                        attribute={attribute}
                        attributeTypeField={attributeTypeField}
                        isSubmitting={isSubmitting}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        onSetSubmitValue={onSetSubmitValue}
                    />
                );
            default:
                return (
                    <div>
                        <Alert variant="danger">
                            The attribute field {attributeTypeField.templateName} has no corresponding input handler!
                        </Alert>
                    </div>
                );
        }
    }, [
        customAttributeFieldData,
        attribute,
        attributeTypeField,
        fieldTouched,
        fieldErrorMessage,
        isSubmitting,
        onBlur,
        setFieldTouched,
        setFieldValue,
        onSetSubmitValue
    ]);

    const isRadioGroup = useMemo(() => {
        return attributeTypeField.templateName === "CustomRadioButton";
    }, [attributeTypeField.templateName]);

    return (
        <div>
            <CustomAttributeTypeFieldInputLabel
                attribute={attribute}
                attributeTypeField={attributeTypeField}
                attributeLabel={customAttributeFieldData.content.label}
                content={content}
                isRadioGroup={isRadioGroup}
            />
            {(attribute.isNew && !attribute.isValueFromPicker) || (attribute.isPlaceholder) ? (
                isRadioGroup ? (
                    <fieldset role={"radiogroup"} aria-live="polite">
                        {InputComponent}
                    </fieldset>
                ) : (
                    <span role={"region"} aria-live="polite">
                        {InputComponent}
                    </span>
                )
            ) : attribute.hasPreSelectedSinglePickerValue ? (
                <ReadOnlyAttributeInput
                    attribute={attribute}
                    fieldName={attributeTypeField.name}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    onAddNewSubmitValue={onAddNewSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                />
            ) : attribute.hasPreviousPickerValues ? (
                <PickerAttributeInput
                    attribute={attribute}
                    fieldName={attributeTypeField.name}
                    fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isSubmitting={isSubmitting}
                    onAddNewSubmitValue={onAddNewSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                />
            ) : null}
        </div>
    );
};